import type { ReactNode } from 'react';

// SlotGridPicker — a FIXED set of named positions laid out on a 2D grid,
// one selectable "active" at a time (story 4-88b; P06 §4, P10 §5, DOD §1.7
// rule 7).
//
// ── WHY A NEW PRIMITIVE ────────────────────────────────────────────────────
// Every existing selection control in this system (`RadioGroup`, `Tabs`,
// `MultiSelect`) picks from a 1D list. The letterhead editor (4-88a's fixed
// 7-slot `ReportLetterhead` shape) needs the opposite: a caller-defined
// SPATIAL layout (some grid positions occupied, some not) where clicking an
// occupied cell marks it "active", and a SEPARATE control (`DirectionalPad`,
// alongside this file) relocates content between cells using that same
// spatial adjacency. No existing primitive expresses "grid position is the
// value" — checked at HEAD, nothing in `components/ui` lays children out by
// row/col with a selectable cell.
//
// ── WHAT THIS PRIMITIVE OWNS vs. WHAT IT DOESN'T ────────────────────────────
// This owns layout + selection only: given `cells` (each with a row/col),
// it places them on a CSS grid and toggles one `activeId`. It knows nothing
// about "letterhead", "logos" or "content" — the caller supplies each cell's
// label and an optional badge, and reads `activeId` back out to drive its
// own per-slot editor. `findAdjacentSlot` (below) is exported alongside the
// component because a directional-relocate caller needs the EXACT SAME
// spatial-adjacency rule the grid itself uses (skip empty positions, stop at
// the first occupied one, or the boundary) — one algorithm, not two that can
// drift apart.
//
// ── A11Y (P10 §2, D44) ──────────────────────────────────────────────────────
//  * Every cell is a real `<button type="button">` — keyboard-operable with
//    Tab + Enter/Space for free, `focus-ring` for a visible focus outline.
//  * `aria-pressed` marks the active cell (a toggle-button semantic, not
//    `aria-selected`'s single-select-listbox semantic — there is no
//    "listbox" here, just N independent toggle buttons where at most one
//    happens to be pressed).
//  * The grid's own `role="group"` + required `aria-label` names the whole
//    control for screen readers ("group" is the correct native role for "a
//    set of related controls with no single-selection widget semantics" —
//    same reasoning `RadioGroup`'s own header comment gives for avoiding a
//    hand-rolled `role="radiogroup"` when the simpler native shape already
//    covers it).
//  * Colors are semantic tokens only (`border-primary`/`bg-primary`/
//    `text-muted-foreground`, no new token) — `contrast-check.mjs` stays
//    green because nothing here is a new pair.

export interface SlotGridCell {
  /** Stable identity — what `activeId`/`onActivate` speak in. */
  id: string;
  /** 1-indexed grid row. */
  row: number;
  /** 1-indexed grid column. */
  col: number;
  label: string;
}

export type GridDirection = 'up' | 'down' | 'left' | 'right';

export interface SlotGridPickerProps {
  cells: SlotGridCell[];
  rows: number;
  cols: number;
  activeId: string | null;
  onActivate: (id: string) => void;
  /** Rendered under a cell's label — e.g. an "assigned" indicator dot. Purely
   * presentational; this primitive has no concept of "assigned" itself. */
  renderCellBadge?: (cell: SlotGridCell) => ReactNode;
  /** Names the control group for screen readers. Required — an unlabelled
   * group of 7 identically-shaped buttons announces as nothing useful. */
  'aria-label': string;
  className?: string;
}

export function SlotGridPicker({
  cells,
  rows,
  cols,
  activeId,
  onActivate,
  renderCellBadge,
  className = '',
  ...rest
}: SlotGridPickerProps): JSX.Element {
  return (
    <div
      role="group"
      aria-label={rest['aria-label']}
      className={`grid gap-2 ${className}`}
      style={{
        gridTemplateRows: `repeat(${rows}, minmax(48px, auto))`,
        gridTemplateColumns: `repeat(${cols}, minmax(64px, 1fr))`,
      }}
    >
      {cells.map((cell) => {
        const active = cell.id === activeId;
        return (
          <button
            key={cell.id}
            type="button"
            aria-pressed={active}
            onClick={() => onActivate(cell.id)}
            style={{ gridRow: cell.row, gridColumn: cell.col }}
            className={`focus-ring flex flex-col items-center justify-center gap-1 rounded-lg border p-2 text-center text-label font-medium leading-tight transition-colors ${
              active
                ? 'border-primary bg-primary/10 text-primary ring-2 ring-primary/30'
                : 'border-dashed border-border text-muted-foreground hover:border-primary/60 hover:text-foreground'
            }`}
          >
            <span>{cell.label}</span>
            {renderCellBadge?.(cell)}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Scans the grid from `fromId` in `direction`, skipping unoccupied
 * positions, and returns the nearest occupied cell's id — or `null` when the
 * grid boundary is reached with nothing found. This is the ONE adjacency
 * rule both `SlotGridPicker` (implicitly, via cell placement) and a
 * directional-relocate control (explicitly, to know which directions are
 * live) share — see the file header for why it lives here rather than being
 * reimplemented per caller.
 */
export function findAdjacentSlot(cells: SlotGridCell[], fromId: string, direction: GridDirection): string | null {
  const from = cells.find((c) => c.id === fromId);
  if (!from) return null;

  const axis: 'row' | 'col' = direction === 'up' || direction === 'down' ? 'row' : 'col';
  const crossAxis: 'row' | 'col' = axis === 'row' ? 'col' : 'row';
  const step = direction === 'up' || direction === 'left' ? -1 : 1;

  const candidates = cells
    .filter((c) => c.id !== fromId && c[crossAxis] === from[crossAxis])
    .filter((c) => (step > 0 ? c[axis] > from[axis] : c[axis] < from[axis]))
    .sort((a, b) => (step > 0 ? a[axis] - b[axis] : b[axis] - a[axis]));

  return candidates[0]?.id ?? null;
}
